import type { ThreadMessage } from '@assistant-ui/react';
import { z } from 'zod';

import type { Recipe } from '#core/lib/expr/session/bindingStore.ts';

import { expressionToAst } from '#plugin-chat/lib/tool/datasetBinding.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

/**
 * Conversation dataset bindings are a **projection of the active
 * message branch**, not a separately-persisted log. The producing
 * expression already lives in every producer tool-call part
 * (`{ outputDataset, expression }`); the success/failure is the same
 * marker `parseResultEnvelope` reads everywhere else. Replaying the
 * recipes this function extracts — in message order — reconstructs the
 * exact binding namespace for whatever branch the user is looking at,
 * so regenerate / edit / branch-switch / reload all stay consistent
 * with the transcript by construction (no second source of truth).
 */

/**
 * The producer tools that stage a dataset binding. Kept in sync with
 * the two `<Tool>` producers — `QueryKnowledgeGraphTool`
 * (`withMention('queryKnowledgeGraph', …)`) and `TransformDatasetTool`
 * (`withMention('transformDataset', …)`). A non-producer tool never
 * carries `{ outputDataset, expression }`, so the schema guard below
 * is the real gate; this set is the cheap pre-filter.
 */
const PRODUCER_TOOL_NAMES: ReadonlySet<string> = new Set(['queryKnowledgeGraph', 'transformDataset']);

/**
 * Both producer input schemas (`QueryKnowledgeGraphInputSchema`,
 * `TransformDatasetInputSchema`) carry these two fields verbatim. We
 * only need the binding-relevant slice; extra keys (`inputDataset`)
 * are ignored. Validated here because persisted parts are a
 * deserialization boundary (older/corrupt shapes must not throw —
 * they're skipped, mirroring the rest of the localStorage layer).
 */
const ProducerArgsSchema = z.object({
  outputDataset: z.string().min(1),
  expression: z.string().min(1),
});

type ToolCallPart = {
  readonly type: 'tool-call';
  readonly toolName?: string;
  readonly args?: unknown;
  readonly result?: unknown;
  readonly isError?: boolean;
};

const isToolCallPart = (part: unknown): part is ToolCallPart =>
  typeof part === 'object' && part !== null && (part as { type?: unknown }).type === 'tool-call';

/**
 * Extract the ordered binding recipes for an active message branch.
 *
 * A part contributes a recipe iff it is a producer tool-call whose
 * result is a **success** envelope and whose args parse to
 * `{ outputDataset, expression }`. Order is message order × part
 * order — the same order the bindings were originally recorded, so a
 * later `(col <earlier> …)` reference resolves on sequential replay.
 *
 * In-progress calls (no result yet), protocol-error calls
 * (`isError`), and structured failures are skipped: parity with the
 * live commit boundary, which only records on a `success: true`
 * envelope. `ThreadMessage` exposes its parts under `.content`.
 */
export const recipesFromMessages = (messages: readonly ThreadMessage[]): Recipe[] =>
  messages.flatMap((message) =>
    (message.content ?? []).flatMap((part): Recipe[] => {
      if (!isToolCallPart(part) || part.toolName === undefined || !PRODUCER_TOOL_NAMES.has(part.toolName)) {
        return [];
      }
      if (part.result === undefined || part.isError === true || parseResultEnvelope(part.result).kind !== 'success') {
        return [];
      }
      const args = ProducerArgsSchema.safeParse(part.args);
      if (!args.success) {
        return [];
      }
      return [{ name: args.data.outputDataset, exprAst: expressionToAst(args.data.expression) }];
    }),
  );
